import { Suspense, useCallback, useState } from "react";

import {
  ChevronDownIcon,
  V2Button,
  V2SingleSelectDropdown,
  V2SingleSelectDropdownItem,
} from "@stacklet/ui";
import { useSearchParams } from "react-router";

import { TableSkeleton } from "app/components/skeletons";

import { GroupUsersGrouping } from "./GroupUsersGrouping";
import { IdpUsersGrouping } from "./IdpUsersGrouping";
import { RoleUsersGrouping } from "./RoleUsersGrouping";
import { UserBrowser } from "./UserBrowser";

/** How the directory is grouped; "none" is the flat user list. */
type GroupBy = "none" | "group" | "role" | "identity-provider";

/** Group-by options, in display order. */
const GROUP_BY_OPTIONS: { id: GroupBy; label: string }[] = [
  { id: "none", label: "None" },
  { id: "group", label: "Group" },
  { id: "role", label: "Role" },
  { id: "identity-provider", label: "Identity Provider" },
];

interface GroupByDropdownProps {
  /** The currently selected grouping. */
  value: GroupBy;
  /** Called when a grouping is chosen. */
  onChange: (value: GroupBy) => void;
}

/** The toolbar dropdown that chooses the grouping. */
function GroupByDropdown({ value, onChange }: GroupByDropdownProps) {
  const active =
    GROUP_BY_OPTIONS.find((option) => option.id === value) ??
    GROUP_BY_OPTIONS[0];

  return (
    <V2SingleSelectDropdown
      AnchorEle={
        <V2Button
          IconRight={<ChevronDownIcon size={12} />}
          aria-label="Group by"
          className="whitespace-nowrap py-xxs"
          variant="tertiary"
        >
          <span className="text-text-secondary">Group by:</span> {active.label}
        </V2Button>
      }
      placement="bottom right"
    >
      {GROUP_BY_OPTIONS.map((option) => (
        <V2SingleSelectDropdownItem
          className="whitespace-nowrap"
          isSelected={option.id === value}
          key={option.id}
          onPress={() => onChange(option.id)}
        >
          {option.label}
        </V2SingleSelectDropdownItem>
      ))}
    </V2SingleSelectDropdown>
  );
}

/**
 * The settings users directory. Ungrouped it's a flat {@link UserBrowser} over
 * every user; grouped (by group/role/identity provider) it's a
 * {@link GroupedUsersGrid} of expandable sections. The group-by dropdown lives
 * in the grid toolbar.
 */
export function UsersDirectory() {
  const [groupBy, setGroupBy] = useState<GroupBy>("none");

  // The search term lives here (in the URL) so it survives switching the
  // grouping, which swaps one child grid for another.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get("q") ?? "";
  const setSearchTerm = useCallback(
    (term: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (term) {
            next.set("q", term);
          } else {
            next.delete("q");
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const toolbarActions = (
    <GroupByDropdown onChange={setGroupBy} value={groupBy} />
  );

  return (
    <Suspense fallback={<TableSkeleton size="medium" />}>
      {groupBy === "none" ? (
        <UserBrowser
          defaultPageSize={25}
          initialSearchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          scope={{ kind: "all" }}
          tableId="users-directory-all"
          title={null}
          toolbarActions={toolbarActions}
          exportTrailing
          hidePagerOnSinglePage
          transparentSearch
        />
      ) : groupBy === "group" ? (
        <GroupUsersGrouping
          initialSearchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          toolbarActions={toolbarActions}
        />
      ) : groupBy === "role" ? (
        <RoleUsersGrouping
          initialSearchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          toolbarActions={toolbarActions}
        />
      ) : (
        <IdpUsersGrouping
          initialSearchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          toolbarActions={toolbarActions}
        />
      )}
    </Suspense>
  );
}
